import React from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';

export type DefinitionCardState = 'pending' | 'saved' | 'not_saved' | 'deleted';

export interface DefinitionCardItem {
  id: string;
  name: string;
  definition: string;
  state: DefinitionCardState;
  /**
   * A definition saved from an earlier chat that this one would replace.
   * While the name matches it, saving updates it; changing the name saves
   * a new definition instead.
   */
  updateTarget?: { id: string; name: string } | null;
  /** A plain-English note, e.g. that a dashboard defines this differently. */
  note?: string | null;
  savedBy?: string | null;
  savedAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface DefinitionCardError {
  message: string;
  /** Set when the save failed because a definition with this name exists. */
  duplicateOf?: { id: string; name: string } | null;
}

export interface DefinitionSaveRequest {
  name: string;
  definition: string;
  mode: 'new' | 'update';
  targetNodeId?: string;
}

export interface DefinitionCardProps {
  item: DefinitionCardItem;
  /** A request is in flight; the controls are held still. */
  busy?: boolean;
  /** What went wrong with the last request. */
  error?: DefinitionCardError | string | null;
  onSave?: (request: DefinitionSaveRequest) => void;
  onDontSave?: () => void;
  onEdit?: (change: { name: string; definition: string }) => void;
  onDelete?: () => void;
  onRestore?: () => void;
  /** Show the definition and its state, with nothing to press. */
  readOnly?: boolean;
}

interface Draft {
  sig: string;
  name: string;
  definition: string;
  editing: boolean;
}

/** A draft holds until the item itself changes — saved, edited, deleted. */
const sigOf = (i: DefinitionCardItem) => [i.state, i.name, i.definition, i.updatedAt ?? ''].join('\u0000');

const freshDraft = (i: DefinitionCardItem): Draft => ({ sig: sigOf(i), name: i.name, definition: i.definition, editing: false });

/**
 * A name as the knowledge base compares it (backend `normalize_name`): lower
 * case, `>` and `%` kept as words, every other run of punctuation or space one
 * space — so "MQL>CAS" matches "mql > cas" and "Hot-Lead" matches "Hot Lead".
 */
const normalizeName = (s: string) =>
  s
    .toLowerCase()
    .replace(/([>%])/g, ' $1 ')
    .replace(/[^a-z0-9%>]+/g, ' ')
    .trim();

const sameName = (a: string, b: string) => normalizeName(a) === normalizeName(b);

function errorOf(e: DefinitionCardError | string | null | undefined): DefinitionCardError | null {
  if (!e) return null;
  return typeof e === 'string' ? { message: e } : e;
}

function formatDay(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * An edit made after the save. Saving over a definition of the same name
 * also stamps it as edited, within a second or two — that is the save, not an edit.
 */
function editedAfterSaving(i: DefinitionCardItem): boolean {
  if (!i.updatedAt) return false;
  const edited = new Date(i.updatedAt).getTime();
  const saved = i.savedAt ? new Date(i.savedAt).getTime() : NaN;
  // The save itself stamps both within a second or two; a person can't edit that fast.
  return Number.isNaN(saved) || Number.isNaN(edited) || edited - saved > 10_000;
}

function savedLine(i: DefinitionCardItem): string {
  const by = i.savedBy ? ` by ${i.savedBy}` : '';
  const on = formatDay(i.savedAt);
  let line = `Saved to the knowledge base${by}${on ? ` on ${on}` : ''}`;
  if (editedAfterSaving(i)) {
    const eBy = i.updatedBy ? ` by ${i.updatedBy}` : '';
    const eOn = formatDay(i.updatedAt);
    line += ` · edited${eBy}${eOn ? ` on ${eOn}` : ''}`;
  }
  return `${line} · available in every chat`;
}

/** Where the definition stands, in its own row above the buttons. Pending says nothing: its buttons do. */
function statusOf(i: DefinitionCardItem): string | null {
  if (i.state === 'saved') return savedLine(i);
  if (i.state === 'not_saved') return 'Not saved';
  if (i.state === 'deleted') return 'Deleted — no longer used';
  return null;
}

/** A plain row under the definition, between dividers: its note or its status. */
function CardRow({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={(t) => ({ px: 1.5, py: 1, color: t.tokens.text.secondary, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
      {children}
    </Box>
  );
}

/**
 * One definition a person taught the chat, shown under the answer it came
 * from: editable, with Save to knowledge base or Don't save, and after that
 * its state — saved, not saved, deleted — with what can be done next. Not
 * saving is final; it still applies for the rest of that chat. A chat that learned several shows one card for each.
 *
 * Shows the same way live and from history; the caller passes the item's
 * current state. Not for definitions that only exist in a dashboard or were
 * entered by an admin: those are changed where they live.
 */
export function DefinitionCard({
  item,
  busy = false,
  error,
  onSave,
  onDontSave,
  onEdit,
  onDelete,
  onRestore,
  readOnly = false,
}: DefinitionCardProps) {
  const [stored, setDraft] = React.useState<Draft>(() => freshDraft(item));
  const d = stored.sig === sigOf(item) ? stored : freshDraft(item);
  const patch = (p: Partial<Draft>) => setDraft({ ...d, ...p });

  const err = errorOf(error);
  // The definition this save would replace: the look-alike the check found,
  // or one the server turned the name down for.
  const target = err?.duplicateOf ?? item.updateTarget ?? null;
  const editing = item.state === 'saved' && d.editing;
  const editable = !readOnly && (item.state === 'pending' || editing);
  const updating = editable && !editing && !!target && sameName(d.name, target.name);
  const canSave = !!d.name.trim() && !!d.definition.trim();
  const status = editing ? null : statusOf(item);
  const label = item.name || 'Definition';

  const save = () =>
    onSave?.({
      name: d.name.trim(),
      definition: d.definition.trim(),
      mode: updating && target ? 'update' : 'new',
      ...(updating && target ? { targetNodeId: target.id } : {}),
    });

  return (
    <Box
      role="group"
      aria-label={label}
      aria-busy={busy || undefined}
      sx={(t) => ({
        border: `1px solid ${t.tokens.border.default}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(t) => ({
          px: 1.5,
          py: 1,
          bgcolor: t.tokens.brand.primarySofter,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* The bulb sits ~4px inside its 20px box; pull it out so its edge lines up with the line below. */}
          <LightbulbOutlined fontSize="small" sx={(t) => ({ color: t.tokens.text.accent, ml: -0.5 })} aria-hidden />
          <Typography variant="subtitle2" component="p" sx={{ m: 0 }}>
            New definition found
          </Typography>
        </Box>
        {/* Only while undecided; afterwards the status row says what happened.
            Starts at the card's edge, under the icon, not under the title. */}
        {item.state === 'pending' && (
          <Typography variant="body2" sx={(t) => ({ mt: 0.25, color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
            Save it to the knowledge base to apply it across your organisation.
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {editable ? (
          <>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={d.name}
              disabled={busy}
              onChange={(e) => patch({ name: e.target.value })}
            />
            <TextField
              label="Meaning"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={d.definition}
              disabled={busy}
              onChange={(e) => patch({ definition: e.target.value })}
            />
          </>
        ) : (
          <Box sx={{ opacity: item.state === 'deleted' ? 0.65 : 1 }}>
            <Typography
              variant="subtitle2"
              component="p"
              sx={{ m: 0, overflowWrap: 'anywhere', textDecoration: item.state === 'deleted' ? 'line-through' : 'none' }}
            >
              {item.name}
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
              {item.definition}
            </Typography>
          </Box>
        )}

        {err && (
          <Typography role="alert" variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger, overflowWrap: 'anywhere' })}>
            {err.message}
          </Typography>
        )}
      </Box>

      {/* The note helps decide; once decided, it has done its job. */}
      {((item.note && item.state === 'pending') || updating) && (
        <CardRow>
          <Typography variant="caption" component="p" sx={{ m: 0, overflowWrap: 'anywhere', fontStyle: 'italic' }}>
            Note: {item.note && item.state === 'pending' ? `${item.note} ` : ''}
            {updating && target && `Saving will update “${target.name}”. To keep both, change the name.`}
          </Typography>
        </CardRow>
      )}

      {status && (
        <CardRow>
          <Typography variant="caption" component="p" sx={{ m: 0, overflowWrap: 'anywhere' }}>{status}</Typography>
        </CardRow>
      )}

      {!readOnly && (
        <CardActions
          item={item}
          editing={d.editing}
          busy={busy}
          canSave={canSave}
          onSave={onSave && save}
          onDontSave={onDontSave}
          onStartEdit={onEdit && (() => patch({ editing: true }))}
          onSaveEdit={onEdit && (() => onEdit({ name: d.name.trim(), definition: d.definition.trim() }))}
          onCancelEdit={() => setDraft(freshDraft(item))}
          onDelete={onDelete}
          onRestore={onRestore}
        />
      )}
    </Box>
  );
}

/** The card's last row: what can be done with it, on its own. */
function CardActions({
  item,
  editing,
  busy,
  canSave,
  onSave,
  onDontSave,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onRestore,
}: {
  item: DefinitionCardItem;
  editing: boolean;
  busy: boolean;
  canSave: boolean;
  onSave?: () => void;
  onDontSave?: () => void;
  onStartEdit?: () => void;
  onSaveEdit?: () => void;
  onCancelEdit: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
}) {
  const spinner = busy ? <CircularProgress size={14} color="inherit" aria-hidden /> : undefined;
  const buttons: React.ReactNode[] = [];

  // Don't save is final: a skipped definition offers nothing more.
  if (item.state === 'pending') {
    if (onSave) {
      buttons.push(
        <Button key="save" size="small" variant="contained" startIcon={spinner} disabled={busy || !canSave} onClick={onSave}>
          Save to knowledge base
        </Button>,
      );
    }
    if (onDontSave) {
      buttons.push(
        <Button key="skip" size="small" variant="outlined" disabled={busy} onClick={onDontSave}>
          Don’t save
        </Button>,
      );
    }
  } else if (item.state === 'saved') {
    if (editing) {
      if (onSaveEdit) {
        buttons.push(
          <Button key="save-edit" size="small" variant="contained" startIcon={spinner} disabled={busy || !canSave} onClick={onSaveEdit}>
            Save changes
          </Button>,
        );
      }
      buttons.push(
        <Button key="cancel" size="small" variant="text" disabled={busy} onClick={onCancelEdit}>
          Cancel
        </Button>,
      );
    } else {
      if (onStartEdit) {
        buttons.push(
          <Button key="edit" size="small" variant="outlined" disabled={busy} onClick={onStartEdit}>
            Edit
          </Button>,
        );
      }
      if (onDelete) {
        buttons.push(
          <Button key="delete" size="small" variant="outlined" color="error" startIcon={spinner} disabled={busy} onClick={onDelete}>
            Delete
          </Button>,
        );
      }
    }
  } else if (item.state === 'deleted' && onRestore) {
    buttons.push(
      <Button key="restore" size="small" variant="outlined" startIcon={spinner} disabled={busy} onClick={onRestore}>
        Restore
      </Button>,
    );
  }

  if (buttons.length === 0) return null;
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 1.5,
        py: 1,
        borderTop: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
      })}
    >
      {buttons}
    </Box>
  );
}
